import React, { useState } from 'react';

import { navigate } from '@reach/router';
import {
  useGetEmailEffectivenessGroupedByFlowBlueprintQuery,
  useGetEmailEffectivenessGroupedByFlowV2BlueprintQuery,
} from '~/graphql/types';
import TEST_ID from './index.testid';
import TabBody from '~/components/Tabs/components/TabBody';
import Tabs from '~/components/Tabs';
import GroupedBarChart, { DataType } from '~/components/Charts/GroupedBarChart';
import { Points, tabsConfig } from './constants/tabsConfig';
import Loading from '~/components/Charts/GroupedBarChart/components/Loading';
import { Body, Heading5 } from '~/components/Typography/index';
import Button from '~/components/Button';
import CardHeader from '../CardHeader';
import useToday from '~/hooks/useToday';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import getRows from './utils/getRows';
import { take } from 'ramda';
import getDateRangeOptionsWithEndDate from '../../utils/getDateRangeOptionsWithEndDate';
import withMasonryCard from '../withMasonryCard';
import { LINK_PREFIX } from '~/scenes/Automation/v2/components/Wizard';

export type Props = {
  dataTestId?: string;
  today?: Date;
};

const formatOptions: Intl.NumberFormatOptions = {
  style: 'decimal',
  minimumFractionDigits: 0,
};

const text = {
  heading: 'E-mail effectiviteit',
  noFlowHeader: 'Geen flows',
  noFlowBody: 'Je hebt nog geen flows aangemaakt.',
  noFlowCTA: 'Maak een flow',

  errorHeader: 'Uh-oh 😱',
  errorBody:
    'Het lijkt er op dat er iets mis is gegaan bij het ophalen van jouw flows. Probeer het later opnieuw.',
};

export const LEGACY_FLOW_PATH = '/-/automation/flows/details/';
export const BUILDER_V2_PATH = '/-/automation/flows/builder-v2/update/';

const EmailEffectivenessComp: React.FC<Props> = ({
  dataTestId,
  today: originalToday,
}) => {
  const today = useToday(originalToday);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState<number>(0);

  const options = getDateRangeOptionsWithEndDate(today);
  const dateRange = options[selectedOptionIndex].payload;

  const { id: accountId } = useCurrentAccount();
  const variables = {
    accountId,
    start: dateRange.start,
    end: dateRange.end,
  };

  const { data, loading, error } =
    useGetEmailEffectivenessGroupedByFlowBlueprintQuery({
      variables,
    });

  const {
    data: dataV2,
    loading: loadingV2,
    error: errorV2,
  } = useGetEmailEffectivenessGroupedByFlowV2BlueprintQuery({
    variables,
  });

  const FilledCardHeader = () => (
    <CardHeader
      headingText={text.heading}
      onOptionChange={({ selectedOptionIdx }) =>
        setSelectedOptionIndex(selectedOptionIdx)
      }
      options={options}
      selectedOptionIndex={selectedOptionIndex}
      dataTestId={TEST_ID.CARD_HEADER}
    />
  );

  if (loading || loadingV2) {
    return (
      <>
        <FilledCardHeader />
        <Loading />
      </>
    );
  }

  if (error || errorV2) {
    return (
      <>
        <FilledCardHeader />
        <Heading5>{text.errorHeader}</Heading5>
        <Body>{text.errorBody}</Body>
      </>
    );
  }

  const noFlowsExist =
    data &&
    data.getEmailEffectivenessGroupedByFlowBlueprint.length <= 0 &&
    dataV2 &&
    dataV2.getEmailEffectivenessGroupedByFlowV2Blueprint.length <= 0;

  if (noFlowsExist) {
    return (
      <>
        <>
          <FilledCardHeader />
          <Heading5>{text.noFlowHeader}</Heading5>
          <Body>{text.noFlowBody}</Body>
          <Button
            size="medium"
            dataTestId={TEST_ID.NO_FLOWS_CTA}
            onClick={() => navigate(`${LINK_PREFIX}/v2/`)}
            label={text.noFlowCTA}
          />
        </>
      </>
    );
  }

  const getChartItems = (points: Points): DataType => {
    const rowsV1 = getRows({
      data: data?.getEmailEffectivenessGroupedByFlowBlueprint,
      points,
      link: LEGACY_FLOW_PATH,
    });

    const rowsV2 = getRows({
      data: dataV2?.getEmailEffectivenessGroupedByFlowV2Blueprint,
      points,
      link: BUILDER_V2_PATH,
    });

    return take(5, rowsV1.concat(rowsV2));
  };

  return (
    <>
      <FilledCardHeader />
      <Tabs dataTestId={`${TEST_ID.CONTAINER}-${TEST_ID.TABS}`}>
        {tabsConfig.map(({ label, points }) => (
          <TabBody label={label} key={label}>
            <GroupedBarChart
              dataTestId={dataTestId}
              data={getChartItems(points)}
              formatOptions={formatOptions}
            />
          </TabBody>
        ))}
      </Tabs>
    </>
  );
};

export default withMasonryCard(EmailEffectivenessComp, {
  containerTestId: TEST_ID.CONTAINER,
});
